import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Search, Trash2, Copy, Check, ExternalLink, Plus } from 'lucide-react';
import { settingsApi } from '../../../api/endpoints/settings';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Modal } from '../../../components/ui/Modal';
import { Select } from '../../../components/ui/Select';

import { useToast } from '../../../components/ui/Alert';
import type { StoreSettings, DomainSearchResult, DnsRecordType } from '../../../types/settings';
import { ApiError } from '../../../types/api';

// Helper: the custom domain lives on `settings.domain` in the backend response;
// `settings.customDomain` is now an alias. Read either for safety.
function getCustomDomain(settings?: StoreSettings): string {
  return settings?.customDomain || settings?.domain || '';
}

type ConnectionMethod = 'nameservers' | 'cname';

const DNS_VALUES = {
  ns1: 'ns1.00stores.com',
  ns2: 'ns2.00stores.com',
  cname: 'cn.00stores.com',
} as const;

const DNS_RECORD_TYPES: DnsRecordType[] = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'SRV', 'CAA'];

// Example value shown as placeholder + format hint per record type.
const DNS_VALUE_PLACEHOLDER: Record<DnsRecordType, string> = {
  A: '203.0.113.5',
  AAAA: '2001:db8::1',
  CNAME: 'target.example.com',
  MX: '10 mail.example.com',
  TXT: 'verification-token=...',
  SRV: '10 20 443 target.example.com',
  CAA: '0 issue "letsencrypt.org"',
};

const PROVIDER_LINKS = [
  { name: 'GoDaddy', url: 'https://www.godaddy.com/help/change-nameservers-for-my-domains-664' },
  { name: 'Namecheap', url: 'https://www.namecheap.com/support/knowledgebase/article.aspx/767/10/how-to-change-dns-for-a-domain/' },
  { name: 'Google Domains', url: 'https://support.google.com/domains/answer/3290309' },
  { name: 'Cloudflare', url: 'https://developers.cloudflare.com/dns/zone-setups/full-setup/setup/' },
  { name: 'Name.com', url: 'https://www.name.com/support/articles/205934457-transferring-dns-to-name-com' },
] as const;

interface Props { settings?: StoreSettings; storeId: string; }

export function DomainTab({ settings, storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currentCustomDomain = getCustomDomain(settings);
  const [customDomain, setCustomDomain] = useState(currentCustomDomain);
  const [searchInput, setSearchInput] = useState('');
  const [searchResult, setSearchResult] = useState<DomainSearchResult | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [connectionMethod, setConnectionMethod] = useState<ConnectionMethod>('nameservers');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      toast('success', t('settings.copiedToClipboard', 'Copied to clipboard'));
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast('error', t('settings.copyFailed', 'Failed to copy'));
    }
  };

  function CopyRow({ label, value, field }: { label: string; value: string; field: string }) {
    const isCopied = copiedField === field;
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border-default bg-bg-field px-3 py-2">
        <div className="min-w-0">
          <span className="text-xs text-text-muted">{label}</span>
          <p className="font-mono text-sm text-text-primary truncate">{value}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleCopy(value, field)}
          className="shrink-0"
        >
          {isCopied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
          {isCopied
            ? t('settings.copied', 'Copied')
            : t('settings.copy', 'Copy')}
        </Button>
      </div>
    );
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['settings', storeId] });

  const updateMutation = useMutation({
    mutationFn: (data: { domain?: string; subdomain?: string }) => settingsApi.updateDomain(storeId, data),
    onSuccess: () => {
      invalidate();
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const searchMutation = useMutation({
    mutationFn: (domain: string) => settingsApi.searchDomain(storeId, domain),
    onSuccess: (data) => setSearchResult(data),
    onError: () => toast('error', t('common.error')),
  });

  const registerMutation = useMutation({
    mutationFn: (domain: string) => settingsApi.registerDomain(storeId, domain),
    onSuccess: () => {
      invalidate();
      toast('success', t('settings.domainRegistered', 'Domain registered successfully'));
      setSearchResult(null);
      setSearchInput('');
    },
    onError: () => toast('error', t('common.error')),
  });

  const removeMutation = useMutation({
    mutationFn: () => settingsApi.removeDomain(storeId),
    onSuccess: () => {
      invalidate();
      toast('success', t('settings.domainRemoved', 'Custom domain removed'));
      setCustomDomain('');
      setShowRemoveConfirm(false);
    },
    onError: () => toast('error', t('common.error')),
  });

  const handleSearch = () => {
    const domain = searchInput.trim();
    if (!domain) return;
    setSearchResult(null);
    searchMutation.mutate(domain);
  };

  // ── Custom DNS records (only meaningful once a custom domain is connected) ──
  const [newRecordType, setNewRecordType] = useState<DnsRecordType>('TXT');
  const [newRecordName, setNewRecordName] = useState('');
  const [newRecordValue, setNewRecordValue] = useState('');

  const dnsRecordsQuery = useQuery({
    queryKey: ['dns-records', storeId],
    queryFn: () => settingsApi.listDnsRecords(storeId),
    enabled: !!currentCustomDomain,
  });

  const addRecordMutation = useMutation({
    mutationFn: () =>
      settingsApi.addDnsRecord(storeId, {
        type: newRecordType,
        name: newRecordName.trim(),
        value: newRecordValue.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dns-records', storeId] });
      toast('success', t('settings.dnsRecordAdded', 'DNS record added'));
      setNewRecordName('');
      setNewRecordValue('');
    },
    onError: (err) =>
      toast('error', err instanceof ApiError ? err.message : t('common.error')),
  });

  const removeRecordMutation = useMutation({
    mutationFn: (recordId: string) => settingsApi.removeDnsRecord(storeId, recordId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dns-records', storeId] });
      toast('success', t('settings.dnsRecordRemoved', 'DNS record removed'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const dnsRecords = dnsRecordsQuery.data?.records ?? [];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Subdomain - always present, not editable. The slug is chosen at store
          creation and used as `${slug}.00stores.com`. */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-3">
        <h2 className="text-base font-semibold">{t('settings.subdomain', 'Subdomain')}</h2>
        <p className="text-xs text-text-muted">
          {t('settings.subdomainDesc', 'Your store is always reachable at this address.')}
        </p>
        <a
          href={`https://${storeId}.00stores.com`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg border border-border-default bg-bg-field px-3 py-2 text-sm text-accent hover:underline"
        >
          {storeId}.00stores.com
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </section>

      {/* Custom Domain */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-base font-semibold">{t('settings.customDomain')}</h2>
          {currentCustomDomain && <Badge variant="success">{t('settings.active')}</Badge>}
        </div>

        {currentCustomDomain ? (
          <>
            <div className="rounded-lg border border-success/25 bg-success/5 px-4 py-3">
              <p className="text-sm font-medium">{currentCustomDomain}</p>
              <p className="text-xs text-text-muted mt-0.5">
                {t('settings.domainActiveDesc', 'Your custom domain is active and serving your store.')}
              </p>
            </div>
            <Input
              label={t('settings.changeDomain', 'Change domain')}
              placeholder="store.example.com"
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button
                onClick={() => updateMutation.mutate({ domain: customDomain })}
                isLoading={updateMutation.isPending}
                disabled={!customDomain.trim() || customDomain === currentCustomDomain}
              >
                {t('common.save')}
              </Button>
              <Button
                variant="danger"
                onClick={() => setShowRemoveConfirm(true)}
              >
                <Trash2 className="h-4 w-4" />
                {t('settings.removeDomain', 'Remove Domain')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <Input
              label={t('settings.customDomain')}
              placeholder="store.example.com"
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
            />
            <Button
              onClick={() => updateMutation.mutate({ domain: customDomain })}
              isLoading={updateMutation.isPending}
              disabled={!customDomain.trim()}
            >
              {t('common.save')}
            </Button>
          </>
        )}
      </section>

      {/* DNS Setup Instructions */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold mb-4">
          {t('settings.dnsSetup', 'DNS Setup Instructions')}
        </h2>
        <p className="text-sm text-text-muted">
          {t(
            'settings.dnsSetupDesc',
            'To connect your custom domain, update your DNS settings at your domain registrar using one of the methods below.',
          )}
        </p>

        <Select
          label={t('settings.connectionMethod', 'Connection method')}
          options={[
            { value: 'nameservers', label: t('settings.nameservers', 'Nameservers (recommended)') },
            { value: 'cname', label: t('settings.cnameRecord', 'CNAME Record') },
          ]}
          value={connectionMethod}
          onChange={(e) => setConnectionMethod(e.target.value as ConnectionMethod)}
        />

        {/* DNS values card */}
        <div className="rounded-lg border border-info/25 bg-info/5 p-4 space-y-3">
          {connectionMethod === 'nameservers' ? (
            <>
              <p className="text-sm font-medium text-text-primary">
                {t(
                  'settings.nsInstructions',
                  'Set the following nameservers at your domain registrar:',
                )}
              </p>
              <div className="space-y-2">
                <CopyRow label="NS1" value={DNS_VALUES.ns1} field="ns1" />
                <CopyRow label="NS2" value={DNS_VALUES.ns2} field="ns2" />
              </div>
              <ol className="list-decimal list-inside space-y-1 text-sm text-text-secondary mt-3">
                <li>{t('settings.nsStep1', 'Log in to your domain registrar (e.g. GoDaddy, Namecheap).')}</li>
                <li>{t('settings.nsStep2', 'Find the DNS or Nameserver settings for your domain.')}</li>
                <li>{t('settings.nsStep3', 'Replace the existing nameservers with the values above.')}</li>
                <li>{t('settings.nsStep4', 'Save changes. Propagation may take up to 48 hours.')}</li>
              </ol>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-text-primary">
                {t(
                  'settings.cnameInstructions',
                  'Add a CNAME record pointing your domain to our servers:',
                )}
              </p>
              <div className="space-y-2">
                <CopyRow
                  label={t('settings.cnameTarget', 'CNAME Target')}
                  value={DNS_VALUES.cname}
                  field="cname"
                />
              </div>
              <ol className="list-decimal list-inside space-y-1 text-sm text-text-secondary mt-3">
                <li>{t('settings.cnameStep1', 'Log in to your domain registrar or DNS provider.')}</li>
                <li>{t('settings.cnameStep2', 'Navigate to DNS records for your domain.')}</li>
                <li>{t('settings.cnameStep3', 'Add a CNAME record with Host "@" or "www" and the target value above.')}</li>
                <li>{t('settings.cnameStep4', 'Save changes. Propagation usually takes a few minutes to a few hours.')}</li>
              </ol>
            </>
          )}
        </div>

        {/* Provider help links */}
        <div className="pt-2">
          <p className="text-xs text-text-muted mb-2">
            {t('settings.providerGuides', 'Setup guides for popular providers:')}
          </p>
          <div className="flex flex-wrap gap-2">
            {PROVIDER_LINKS.map((provider) => (
              <a
                key={provider.name}
                href={provider.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-md border border-border-default bg-bg-tertiary px-2.5 py-1 text-xs text-text-secondary hover:text-text-primary hover:border-border-hover transition-colors"
              >
                {provider.name}
                <ExternalLink className="h-3 w-3" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Custom DNS Records — only for a connected custom domain */}
      {currentCustomDomain && (
        <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
          <div>
            <h2 className="text-base font-semibold">
              {t('settings.dnsRecords', 'Custom DNS Records')}
            </h2>
            <p className="text-xs text-text-muted mt-1">
              {t(
                'settings.dnsRecordsDesc',
                'Add optional records for your domain: extra subdomains (A) or verification tokens (TXT). Only works when your domain is connected via nameservers.',
              )}
            </p>
          </div>

          {/* Existing records */}
          {dnsRecordsQuery.isLoading ? (
            <p className="text-sm text-text-muted">{t('common.loading', 'Loading...')}</p>
          ) : dnsRecords.length > 0 ? (
            <div className="space-y-2">
              {dnsRecords.map((record) => (
                <div
                  key={record.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border-default bg-bg-field px-3 py-2"
                >
                  <div className="min-w-0 flex items-center gap-2">
                    <Badge variant="info">{record.type}</Badge>
                    <div className="min-w-0">
                      <p className="font-mono text-sm text-text-primary truncate">{record.name}</p>
                      <p className="font-mono text-xs text-text-muted truncate">{record.value}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeRecordMutation.mutate(record.id)}
                    isLoading={
                      removeRecordMutation.isPending &&
                      removeRecordMutation.variables === record.id
                    }
                    className="shrink-0"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-danger" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-text-muted">
              {t('settings.dnsRecordsEmpty', 'No custom records yet.')}
            </p>
          )}

          {/* Add record form */}
          <div className="grid gap-3 sm:grid-cols-[7rem_1fr] items-start pt-2 border-t border-border-default">
            <Select
              label={t('settings.dnsRecordType', 'Type')}
              options={DNS_RECORD_TYPES.map((tp) => ({ value: tp, label: tp }))}
              value={newRecordType}
              onChange={(e) => setNewRecordType(e.target.value as DnsRecordType)}
            />
            <div>
              <Input
                label={t('settings.dnsRecordName', 'Name')}
                placeholder={`sub.${currentCustomDomain}`}
                value={newRecordName}
                onChange={(e) => setNewRecordName(e.target.value)}
              />
              <p className="text-xs text-text-muted mt-1">
                {t('settings.dnsRecordNameHint', 'Use @ for the root domain')} ({currentCustomDomain})
              </p>
            </div>
            <div className="sm:col-span-2">
              <Input
                label={t('settings.dnsRecordValue', 'Value')}
                placeholder={DNS_VALUE_PLACEHOLDER[newRecordType]}
                value={newRecordValue}
                onChange={(e) => setNewRecordValue(e.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <Button
                onClick={() => addRecordMutation.mutate()}
                isLoading={addRecordMutation.isPending}
                disabled={!newRecordName.trim() || !newRecordValue.trim()}
              >
                <Plus className="h-4 w-4" />
                {t('settings.addDnsRecord', 'Add Record')}
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Domain Search & Registration */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold mb-4">
          {t('settings.registerDomain', 'Register a New Domain')}
        </h2>
        <p className="text-sm text-text-muted">
          {t('settings.registerDomainDesc', 'Search for a domain name and register it directly for your store.')}
        </p>

        <div className="flex items-end gap-2">
          <Input
            label={t('settings.domainName', 'Domain name')}
            placeholder="mystore.com"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setSearchResult(null);
            }}
            className="flex-1"
          />
          <Button
            onClick={handleSearch}
            isLoading={searchMutation.isPending}
            disabled={!searchInput.trim()}
          >
            <Search className="h-4 w-4" />
            {t('settings.checkAvailability', 'Check')}
          </Button>
        </div>

        {searchResult && (
          <div className={`rounded-lg border px-4 py-3 ${
            searchResult.available
              ? 'border-success/25 bg-success/5'
              : 'border-danger/25 bg-danger/5'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{searchResult.domain}</span>
                  <Badge variant={searchResult.available ? 'success' : 'danger'}>
                    {searchResult.available
                      ? t('settings.available', 'Available')
                      : t('settings.unavailable', 'Unavailable')}
                  </Badge>
                </div>
                {searchResult.available && searchResult.price && (
                  <p className="text-xs text-text-muted mt-1">
                    {t('settings.registrationPrice', 'Registration')}: {searchResult.price.registration} {searchResult.price.currency}/yr
                    {' · '}
                    {t('settings.renewalPrice', 'Renewal')}: {searchResult.price.renewal} {searchResult.price.currency}/yr
                  </p>
                )}
              </div>
              {searchResult.available && (
                <Button
                  size="sm"
                  onClick={() => registerMutation.mutate(searchResult.domain)}
                  isLoading={registerMutation.isPending}
                >
                  {t('settings.registerNow', 'Register')}
                </Button>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Remove Domain Confirmation */}
      <Modal
        isOpen={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        title={t('settings.removeDomain', 'Remove Domain')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowRemoveConfirm(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => removeMutation.mutate()}
              isLoading={removeMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
              {t('settings.removeDomain', 'Remove Domain')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">
          {t(
            'settings.removeDomainConfirm',
            'Are you sure you want to remove your custom domain? Your store will only be accessible via the subdomain.',
          )}
        </p>
        {currentCustomDomain && (
          <p className="mt-2 font-medium">{currentCustomDomain}</p>
        )}
      </Modal>
    </div>
  );
}
